import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, Mail, Lock, User, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const DEMOS = [
  { label: "Salaried Pro", email: "salaried@epitaka.app" },
  { label: "Freelancer", email: "freelancer@epitaka.app" },
  { label: "Executive", email: "executive@epitaka.app" },
];

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success("Welcome to ePitaka!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async (demoEmail) => {
    setLoading(true);
    try {
      await login(demoEmail, "demo1234");
      toast.success("Signed in with demo account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-600/20 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="relative flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
            <Wallet className="w-5 h-5" />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">ePitaka</span>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Give every dollar<br />a <span className="text-orange-500">purpose.</span>
          </h1>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Allocate income into five smart wallets, track planned vs actual money movement,
            and watch your monthly financial health improve.
          </p>
          <div className="flex flex-wrap gap-2 max-w-md">
            {["Bills", "Savings", "Emergency", "Unplanned", "My Wallet"].map((w) => (
              <span key={w} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 border border-white/10">{w}</span>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-400">Budget-conscious clarity for salaried professionals.</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center text-white">
              <Wallet className="w-5 h-5" />
            </div>
            <span className="font-display text-xl font-bold">ePitaka</span>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-slate-500 mt-1.5 text-sm">
            {mode === "login" ? "Sign in to your Smart Wallet" : "Start budgeting in minutes"}
          </p>

          <div className="mt-6 grid grid-cols-2 p-1 bg-slate-100 rounded-xl text-sm font-medium">
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`py-2 rounded-lg transition-colors ${mode === "login" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
            >Sign In</button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              className={`py-2 rounded-lg transition-colors ${mode === "register" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
            >Sign Up</button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="name" data-testid="auth-name-input" className="pl-9" placeholder="Alex Rivera"
                    value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" data-testid="auth-email-input" type="email" className="pl-9" placeholder="you@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="password" data-testid="auth-password-input" type="password" className="pl-9" placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              </div>
            </div>
            <Button type="submit" data-testid="auth-submit-button" disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11 font-semibold group">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button variant="outline" data-testid="google-login-button" onClick={googleLogin}
            className="w-full h-11 border-slate-300 font-medium">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>

          <div className="mt-6">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-orange-500" /> Quick demo accounts
            </p>
            <div className="grid grid-cols-3 gap-2">
              {DEMOS.map((d) => (
                <button key={d.email} data-testid={`demo-user-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => demoLogin(d.email)} disabled={loading}
                  className="text-xs font-medium py-2 px-1 rounded-lg border border-slate-200 hover:border-orange-400 hover:bg-orange-50 text-slate-700 transition-colors">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
